import { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PageHeader, EmptyState } from '../components/SectionHeader';
import { ProfessionalCard, CompanyCard } from '../components/Cards';
import { CATEGORIES } from '../data/demo';
import { Pagination, usePagination } from '../components/Pagination';
import { useI18n } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ListingPage = ({ mode }) => {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const url = mode === 'pros'
      ? `${API}/directory/professionals${cat !== 'all' ? `?cat=${encodeURIComponent(cat)}` : ''}`
      : `${API}/directory/companies`;
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => { if (active) setData(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (active) setData([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [mode, cat]);

  const filtered = useMemo(() => data.filter((x) => {
    if (!q) return true;
    const hay = mode === 'pros' ? `${x.name} ${x.role || ''} ${(x.skills || []).join(' ')}` : `${x.name} ${(x.specialties || []).join(' ')} ${x.description || ''}`;
    return hay.toLowerCase().includes(q.toLowerCase());
  }), [q, data, mode]);
  const pagination = usePagination(filtered, 6, `${mode}|${q}|${cat}`);

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          title={mode === 'pros' ? t('nav.profesionales') : t('nav.empresas')}
          subtitle={mode === 'pros' ? t('section.pros.sub') : t('section.companies.sub')}
          testId={`${mode}-header`}
        />
        <div className="sc-card p-4 mb-6 flex flex-col md:flex-row gap-3">
          <input
            data-testid={`${mode}-search-input`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={mode === 'pros' ? 'Nombre, especialidad, herramientas...' : 'Empresa, especialidad...'}
            className="bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white flex-1 text-sm"
          />
        </div>

        {mode === 'pros' && (
          <div className="flex gap-2 overflow-x-auto pb-3 sc-scrollbar-hide mb-6">
            <button onClick={() => setCat('all')} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full border whitespace-nowrap transition-colors ${cat === 'all' ? 'bg-amber-500 text-slate-950 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/40'}`}>Todos</button>
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full border whitespace-nowrap transition-colors ${cat === c.id ? 'bg-amber-500 text-slate-950 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/40'}`}>{c.name}</button>
            ))}
          </div>
        )}

        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">{filtered.length} {t('meta.results')}</div>

        {loading ? (
          <div className="py-24 text-center text-slate-500 font-mono text-sm" data-testid={`${mode}-loading`}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title={mode === 'pros' ? 'Aún no hay profesionales' : 'Aún no hay empresas'} subtitle={mode === 'pros' ? 'Crea tu perfil profesional y aparecerás aquí para que las empresas te encuentren.' : 'Registra tu empresa y completa tus datos para aparecer en el directorio.'} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {mode === 'pros'
              ? pagination.items.map((p) => <ProfessionalCard key={p.id} pro={p} />)
              : pagination.items.map((c) => <CompanyCard key={c.id} company={c} />)}
          </div>
        )}
        <Pagination {...pagination} testId={mode} />
      </div>
      <Footer />
    </div>
  );
};

export const ProfesionalesPage = () => <ListingPage mode="pros" />;
export const EmpresasPage = () => <ListingPage mode="companies" />;
